<template>
  <Loader :value="promises.fetchOne" :message="$metaInfo.title" class="contact-dashboard">
    <div class="container view">
      <HeaderComponent :title="$metaInfo.title" :breadcrumb="breadcrumbs">
        <template #actions>
          <TaskCreateButton :reference="{ onModel: 'Contact', ref: id }" />
          <TaskButtonList v-model="item.tasks" class="ml-2" :reference-id="id" />
          <Actions placement="contact" :query="{ contact: id }" />
          <ButtonComponent
            :icon="['fal', 'sync-alt']"
            :label="$t('syncUser')"
            :promise="promises.fetchOne"
            @click="fetch({ id })"
          />
        </template>
      </HeaderComponent>
      <div class="contact-dashboard__content">
        <Comments :value="item.comments" @submit="comment" :promise="commentPromise" />
        <ContactCard :value="item" />
        <Contracts :value="item.contracts">
          <template #after-title>
            <router-link
              v-if="item.id"
              :to="{ name: 'custom-contract-create', query: { contact: item.id, returnTo: $route.path } }"
            >
              {{ $t('createContract') }}
            </router-link>
          </template>
        </Contracts>
        <Attachments :value="item.comments" />
        <div v-if="item.leads && item.leads.length" class="contact-dashboard__leads">
          <label style="display: block">{{ $t('isInterestedIn') }}:</label>
          <Lead v-for="item in item.leads" :value="item" :key="item.id" />
        </div>
      </div>
      <RemoveComponent
        :show.sync="removeDialog"
        :title="$metaInfo.title"
        :action="() => remove({ id })"
        @onSuccess="$router.push({ name: 'dashboard' })"
        :resource="$tc('contact')"
      />
    </div>
  </Loader>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import { mapi18n } from '@/i18n'
import CommentApi from '@/api/Comment'

import { InputComponent } from 'vue-elder-input'
import { DropdownComponent } from 'vue-elder-dropdown'
import { CheckboxComponent } from 'vue-elder-checkbox'
import Attachments from '@/components/Comment/Attachments'
import RemoveComponent from '@/components/Remove'
import ContactCard from '@/components/Contact/Card'
import Contracts from '@/components/Contract/Contracts'
import Comments from '@/components/Comment/List'
import HeaderComponent from '@/components/Header'
import Card from '@/components/Card'
import Lead from './Lead'
import TaskButtonList from '@/components/Task/ButtonList'
import TaskCreateButton from '@/components/Task/CreateButton'
import Actions from '@/components/Actions'

export default {
  props: {
    id: String,
  },
  data() {
    return {
      removeDialog: false,
      commentPromise: null,
    }
  },
  watch: {
    id: {
      handler: 'fetch',
      immediate: true,
    },
  },
  computed: {
    ...mapState('Contact', ['item', 'promises']),
    ...mapi18n('i18n', {
      loading() {
        return this.$tc('loading') + '...'
      },
      loadingContact() {
        return this.$t('loading', { resource: this.$tc('contact', 1) }) + '...'
      },
    }),
    breadcrumbs() {
      return [{ title: this.$tc('contact', 2), action: { name: 'contacts' } }, { title: this.$metaInfo.title }]
    },
  },
  methods: {
    ...mapMutations('Contact', ['set', 'addComment']),
    ...mapActions('Contact', {
      fetch(dispatch) {
        if (!this.id) return
        return dispatch('fetch', [{ id: this.id }, { query: 'ContactDashboard' }])
      },
      remove: 'remove',
    }),
    comment(data) {
      this.commentPromise = CommentApi.mutate({
        reference: { onModel: 'Contact', ref: this.id },
        ...data,
      }).then(data => this.addComment(data))
    },
  },
  metaInfo() {
    return {
      title: this.$path('item.name') || this.i18n.loadingContact,
    }
  },
  components: {
    InputComponent,
    HeaderComponent,
    DropdownComponent,
    CheckboxComponent,
    ContactCard,
    Comments,
    RemoveComponent,
    Card,
    Lead,
    TaskButtonList,
    TaskCreateButton,
    Attachments,
    Actions,
    Contracts,
  },
}
</script>

<style lang="scss">
.contact-dashboard {
  background-color: $background-grey;

  &__content {
    display: grid;
    grid-gap: 2rem;
    grid-template-columns: 1fr 350px;
    grid-template-rows: auto auto auto 1fr;
    align-items: flex-start;

    .comments {
      grid-row-end: span 4;
    }
  }

  &__leads {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__form {
    margin: 0 auto;
    max-width: 550px;
  }

  &__inputs {
    display: grid;
    align-items: center;
    grid-template-columns: 1fr;
    grid-gap: $form-gap-row / 2 $form-gap-column;
    align-items: flex-start;
  }

  [type='submit'] {
    margin-right: auto;
  }

  [data-field='references'] {
    .elder-input__element {
      padding: 0;
    }
    .select-checklist__items {
      padding: 1.1em;
      max-height: 300px;
      overflow-y: auto;

      @include scrollShadow();
    }
  }
}
</style>
